import cx from "classnames";
import { FunctionComponent, useContext } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";

import Checker, { CheckerStatus } from "./Checker";
import { Color, GameMode, MovementDirection, Player } from "./Types";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import {
  setMovementDirection,
  setPlayerOneColor,
  setShowSettingsMenu,
  setAutoDice,
  setSoundEnabled,
  setSoundVolume,
} from "./store/settingsSlice";
import { ActionsContext, LocalGameActions } from "./ActionsContext";
import SoundService from "./services/SoundService";

type SettingsMenuProps = {
  playerPerspective: Player;
};

const SettingsMenu: FunctionComponent<SettingsMenuProps> = ({
  playerPerspective,
}) => {
  const settings = useAppSelector((state) => state.settings);
  const actions  = useContext(ActionsContext);
  const dispatch = useAppDispatch();

  const playerOneColor = settings.playerOneColor;
  const playerTwoColor = playerOneColor === Color.White ? Color.Black : Color.White;
  const firstColor     = playerPerspective === Player.One ? playerOneColor : playerTwoColor;
  const secondColor    = firstColor === Color.White ? Color.Black : Color.White;

  const firstLabel  = actions instanceof LocalGameActions
    ? (playerPerspective === Player.One ? "Игрок 1" : "Игрок 2")
    : "Вы";
  const secondLabel = actions instanceof LocalGameActions
    ? (playerPerspective === Player.One ? "Игрок 2" : "Игрок 1")
    : "Соперник";

  const close = () => dispatch(setShowSettingsMenu(false));

  const toggleColor = () => {
    dispatch(setPlayerOneColor(playerOneColor === Color.Black ? Color.White : Color.Black));
  };

  const toggleDirection = () => {
    dispatch(setMovementDirection(
      settings.movementDirection === MovementDirection.Clockwise
        ? MovementDirection.CounterClockwise
        : MovementDirection.Clockwise
    ));
  };

  const handleVolumeChange = (v: number) => {
    dispatch(setSoundVolume(v));
    SoundService.instance.setVolume(v);
    SoundService.instance.play("click");
  };

  return (
    <Modal
      show={settings.showSettingsMenu}
      centered
      dialogClassName="Settings-menu"
      keyboard
      onHide={close}
    >
      <Modal.Header>
        <Modal.Title>Настройки</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Цвет шашек */}
        <div className="Settings-option-row">
          <div className="Settings-label">Цвет шашек</div>
          <div className="Settings-color-row" onClick={toggleColor}>
            <div className="Settings-player-col">
              <div className="Settings-player-name">{firstLabel}</div>
              <div className="Settings-checker-mini">
                <Checker color={firstColor} checkerPulse={false}
                  status={CheckerStatus.None} onAnimationComplete={() => {}} animation={null} />
              </div>
            </div>
            <div className="Settings-swap-btn">⇄</div>
            <div className="Settings-player-col">
              <div className="Settings-player-name">{secondLabel}</div>
              <div className="Settings-checker-mini">
                <Checker color={secondColor} checkerPulse={false}
                  status={CheckerStatus.None} onAnimationComplete={() => {}} animation={null} />
              </div>
            </div>
          </div>
        </div>

        {/* Направление движения (не показываем в длинных нардах — всегда CCW) */}
        {settings.gameMode !== GameMode.LongNardy && (
          <div className="Settings-option-row">
            <div className="Settings-label">Направление</div>
            <div
              className={cx("Settings-option-movement-diagram", {
                light: firstColor === Color.White,
                dark:  firstColor === Color.Black,
                cw: settings.movementDirection === MovementDirection.Clockwise,
              })}
              onClick={toggleDirection}
            />
          </div>
        )}

        {/* Автобросок */}
        <div className="Settings-option-row">
          <div className="Settings-label">Автобросок кубиков</div>
          <button
            className={`Settings-toggle ${settings.autoDice ? "on" : "off"}`}
            onClick={() => dispatch(setAutoDice(!settings.autoDice))}
          >
            {settings.autoDice ? "Вкл" : "Выкл"}
          </button>
        </div>

        {/* Звук */}
        <div className="Settings-option-row">
          <div className="Settings-label">Звук</div>
          <button
            className={`Settings-toggle ${settings.soundEnabled ? "on" : "off"}`}
            onClick={() => {
              const next = !settings.soundEnabled;
              dispatch(setSoundEnabled(next));
              SoundService.instance.setEnabled(next);
            }}
          >
            {settings.soundEnabled ? "Вкл" : "Выкл"}
          </button>
        </div>

        {/* Громкость */}
        {settings.soundEnabled && (
          <div className="Settings-option-row">
            <div className="Settings-label">Громкость</div>
            <input
              className="Settings-slider"
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={settings.soundVolume}
              onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
            />
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" onClick={close}>Закрыть</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SettingsMenu;
